const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);
const TRAILING_DOT_PATTERN = /\.$/;

function normalizeHostname(hostname: string): string {
  return hostname.trim().replace(TRAILING_DOT_PATTERN, '').toLowerCase();
}

/**
 * Returns true if the provided hostname represents a localhost / loopback address.
 * Accepts dotted variants (127.*) and subdomains of localhost used by Vercel/Next dev servers.
 */
export function isLoopbackHostname(hostname: string | null | undefined): boolean {
  if (!hostname) {
    return false;
  }

  const normalized = normalizeHostname(hostname);

  if (LOOPBACK_HOSTNAMES.has(normalized)) {
    return true;
  }

  if (normalized.startsWith('127.')) {
    return true;
  }

  if (normalized.endsWith('.localhost')) {
    return true;
  }

  return normalized.includes('localhost');
}

function getBrowserHostname(): string | null {
  if (globalThis.window === undefined) {
    return null;
  }

  return globalThis.location.hostname;
}

/**
 * Determines if the current browser context is served from a localhost origin.
 */
export function isBrowserLocalhost(): boolean {
  return isLoopbackHostname(getBrowserHostname());
}

export function composeBrowserUrl(pathname?: string | null, search?: string | null): string | null {
  if (globalThis.window === undefined) {
    return null;
  }

  const { origin, pathname: currentPathname, search: currentSearch } = globalThis.location;
  const resolvedPathname = (pathname ?? currentPathname) || '/';
  const rawSearch = search ?? currentSearch;

  if (!rawSearch || rawSearch === '?') {
    return `${origin}${resolvedPathname}`;
  }

  const normalizedSearch = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  if (!normalizedSearch) {
    return `${origin}${resolvedPathname}`;
  }

  return `${origin}${resolvedPathname}?${normalizedSearch}`;
}
